import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { ClipboardListIcon, FileEditIcon, SearchIcon, FilterIcon, VideoIcon, CheckCircleIcon } from 'lucide-react';
interface Step {
  icon: React.ReactNode;
  number: string;
  title: string;
  description: string;
}
const steps: Step[] = [{
  icon: <ClipboardListIcon className="w-6 h-6" data-id="element-375" />,
  number: '01',
  title: 'Бриф',
  description: 'Вивчаємо потреби компанії, формуємо профіль ідеального кандидата'
}, {
  icon: <FileEditIcon className="w-6 h-6" data-id="element-376" />,
  number: '02',
  title: 'Вакансія',
  description: 'Складаємо привабливий опис та стратегію пошуку'
}, {
  icon: <SearchIcon className="w-6 h-6" data-id="element-377" />,
  number: '03',
  title: 'Пошук',
  description: 'Активний хантинг по базі 100 000+ кандидатів та відкритих джерелах'
}, {
  icon: <FilterIcon className="w-6 h-6" data-id="element-378" />,
  number: '04',
  title: 'Відбір',
  description: 'Проводимо скринінг, оцінюємо компетенції та мотивацію'
}, {
  icon: <VideoIcon className="w-6 h-6" data-id="element-379" />,
  number: '05',
  title: "Інтерв'ю",
  description: "Організовуємо зустрічі з фіналістами, збираємо зворотний зв'язок"
}, {
  icon: <CheckCircleIcon className="w-6 h-6" data-id="element-380" />,
  number: '06',
  title: 'Офер',
  description: 'Допомагаємо узгодити умови та супроводжуємо вихід на роботу'
}];
export function ProcessSteps() {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  return <section className="py-20 lg:py-28 bg-white" id="process" data-id="element-381">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref} data-id="element-382">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5
      }} className="text-center mb-16" data-id="element-383">
          <span className="text-sm font-semibold text-primary-600 tracking-wide uppercase" data-id="element-384">
            Як ми працюємо
          </span>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-navy-900 tracking-tight" data-id="element-385">
            Етапи підбору
          </h2>
          <p className="mt-4 text-lg text-navy-500 max-w-2xl mx-auto" data-id="element-386">
            Прозорий процес від першого дзвінка до виходу кандидата на роботу
          </p>
        </motion.div>

        {/* Desktop: horizontal timeline */}
        <div className="hidden lg:block" data-id="element-387">
          <div className="relative" data-id="element-388">
            {/* Connecting line */}
            <motion.div initial={{
            scaleX: 0
          }} animate={isInView ? {
            scaleX: 1
          } : {}} transition={{
            duration: 1.2,
            delay: 0.3,
            ease: 'easeOut'
          }} className="absolute top-10 left-[8%] right-[8%] h-0.5 bg-gradient-to-r from-primary-200 via-primary-400 to-primary-200 origin-left" data-id="element-389" />

            <div className="grid grid-cols-6 gap-4" data-id="element-390">
              {steps.map((step, index) => <motion.div key={step.number} initial={{
              opacity: 0,
              y: 30
            }} animate={isInView ? {
              opacity: 1,
              y: 0
            } : {}} transition={{
              duration: 0.5,
              delay: 0.2 + index * 0.12
            }} className="flex flex-col items-center text-center" data-id="element-391">
                  {/* Step circle */}
                  <div className="relative z-10 w-20 h-20 rounded-2xl bg-white border-2 border-primary-200 flex items-center justify-center text-primary-600 shadow-sm" data-id="element-392">
                    {step.icon}
                  </div>
                  <span className="mt-4 text-xs font-bold text-primary-500 tracking-widest" data-id="element-393">
                    КРОК {step.number}
                  </span>
                  <h3 className="mt-2 text-base font-bold text-navy-900" data-id="element-394">
                    {step.title}
                  </h3>
                  <p className="mt-2 text-sm text-navy-500 leading-relaxed" data-id="element-395">
                    {step.description}
                  </p>
                </motion.div>)}
            </div>
          </div>
        </div>

        {/* Mobile/Tablet: vertical timeline */}
        <div className="lg:hidden space-y-0" data-id="element-396">
          {steps.map((step, index) => <motion.div key={step.number} initial={{
          opacity: 0,
          x: -20
        }} animate={isInView ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.4,
          delay: index * 0.1
        }} className="relative flex gap-5 pb-10 last:pb-0" data-id="element-397">
              {/* Vertical line */}
              {index < steps.length - 1 && <div className="absolute left-6 top-14 bottom-0 w-0.5 bg-primary-200" data-id="element-398" />}
              {/* Icon */}
              <div className="relative z-10 flex-shrink-0 w-12 h-12 rounded-xl bg-primary-100 text-primary-600 flex items-center justify-center" data-id="element-399">
                {step.icon}
              </div>
              <div data-id="element-400">
                <span className="text-xs font-bold text-primary-500 tracking-widest" data-id="element-401">
                  КРОК {step.number}
                </span>
                <h3 className="mt-1 text-lg font-bold text-navy-900" data-id="element-402">
                  {step.title}
                </h3>
                <p className="mt-1 text-sm text-navy-500" data-id="element-403">{step.description}</p>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}